import { format, isSameYear, isToday, isTomorrow, parseISO } from 'date-fns';
import pluralize from 'pluralize';
import { useSearchParams } from 'react-router-dom';

import { FreePlanGate } from '@/modules/billing/components/FreePlanGate';
import { FreePlanAutomationCta } from '@/modules/hiring-manager/components/freePlanAutomationCta/FreePlanAutomationCta';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import {
  PortalList,
  PortalListSkeleton,
} from '@/modules/hiring-manager/components/PortalList';
import {
  INTERVIEWS_PAGE_SIZE,
  useInfiniteInterviewsQuery,
} from '@/modules/hiring-manager/queries/useInfiniteInterviewsQuery';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { type Interview, InterviewRow } from './InterviewRow';
import { InterviewsEmpty } from './InterviewsEmpty';

type Feed = ReturnType<typeof useInfiniteInterviewsQuery>;

const dayLabel = (date: Date) => {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(
    date,
    isSameYear(date, new Date()) ? 'EEE MMM d' : 'EEE MMM d, yyyy',
  );
};

const overviewDescription = 'Upcoming and past interviews for all your jobs.';

function PageHeader({ description }: { description: string }) {
  return (
    <div>
      <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
        Interviews
      </h1>
      <p className="text-muted-foreground mt-1 text-[13px]">{description}</p>
    </div>
  );
}

function InterviewsSkeleton() {
  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-72" />
      </div>
      <PortalListSkeleton rows={3} />
    </div>
  );
}

/** Upcoming interviews grouped by day, past ones as one dimmed list. */
export function HMInterviews() {
  const [searchParams] = useSearchParams();
  const currentUserQuery = useCurrentUserQuery();
  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );
  const companyId = tenantSelection.selectedTenantId;
  const upcoming = useInfiniteInterviewsQuery(
    companyId,
    'Upcoming',
    Boolean(companyId),
  );
  const past = useInfiniteInterviewsQuery(
    companyId,
    'Past',
    Boolean(companyId),
  );
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (currentUserQuery.isPending) {
    return <InterviewsSkeleton />;
  }

  // A failed /users/me never reaches this page: ProtectedRoute redirects to
  // /login. A loaded user without a company does, and both interview queries
  // stay `skipToken`-pending forever, so it needs its own branch.
  if (!companyId) {
    return (
      <div className="flex flex-col gap-10">
        <PageHeader description={overviewDescription} />
        <HMLoadError
          description="Your account is not linked to a company yet. Ask your admin to add you to one, then reload this page. If you do not know who that is, email us and we will sort it out."
          supportSubject="No company selected"
          title="No company selected"
        />
      </div>
    );
  }

  if (upcoming.isPending || past.isPending) {
    return <InterviewsSkeleton />;
  }

  const hasError = upcoming.isError || past.isError;
  const isEmpty = !hasError && upcoming.totalCount + past.totalCount === 0;

  return (
    <div className="flex flex-col gap-10">
      <PageHeader
        description={
          hasError
            ? overviewDescription
            : isEmpty
              ? overviewDescription
              : `${upcoming.totalCount} upcoming · times in ${timeZone}`
        }
      />

      {hasError && (
        <HMLoadError
          description="We could not load your interviews. Check your connection and try again."
          isRetrying={upcoming.isFetching || past.isFetching}
          onRetry={() => {
            if (upcoming.isError) void upcoming.refetch();
            if (past.isError) void past.refetch();
          }}
          title="Interviews could not be loaded"
        />
      )}

      {isEmpty ? (
        <FreePlanGate
          upgrade={
            <FreePlanAutomationCta
              surface="interviews"
              to={`/hm/billing${tenantSelection.tenantSearch}`}
            />
          }
        >
          <InterviewsEmpty
            companyId={companyId}
            searchSuffix={tenantSelection.tenantSearch}
            userId={currentUserQuery.data?.id}
          />
        </FreePlanGate>
      ) : (
        <>
          <UpcomingSections
            feed={upcoming}
            tenantSearch={tenantSelection.tenantSearch}
          />
          {past.totalCount > 0 && (
            <section
              aria-labelledby="past-interviews"
              className="flex flex-col gap-3"
            >
              <DayHeader
                count={past.totalCount}
                id="past-interviews"
                label="Past"
              />
              <PortalList>
                {past.interviews.map((interview) => (
                  <InterviewRow
                    interview={interview}
                    isUpcoming={false}
                    key={interview.meeting.id}
                    tenantSearch={tenantSelection.tenantSearch}
                  />
                ))}
              </PortalList>
              <LoadMore feed={past} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

function UpcomingSections({
  feed,
  tenantSearch,
}: {
  feed: Feed;
  tenantSearch: string;
}) {
  if (feed.totalCount === 0) return null;

  const byDay = new Map<string, Interview[]>();
  for (const interview of feed.interviews) {
    const key = format(parseISO(interview.meeting.startsAt), 'yyyy-MM-dd');
    byDay.set(key, [...(byDay.get(key) ?? []), interview]);
  }

  return (
    <>
      {[...byDay.entries()].map(([key, list]) => {
        const label = dayLabel(parseISO(list[0]!.meeting.startsAt));
        return (
          <section
            aria-labelledby={`interviews-${key}`}
            className="flex flex-col gap-3"
            key={key}
          >
            <DayHeader
              count={list.length}
              id={`interviews-${key}`}
              label={label}
            />
            <PortalList>
              {list.map((interview) => (
                <InterviewRow
                  interview={interview}
                  isUpcoming
                  key={interview.meeting.id}
                  tenantSearch={tenantSearch}
                />
              ))}
            </PortalList>
          </section>
        );
      })}
      <LoadMore feed={feed} />
    </>
  );
}

function DayHeader({
  count,
  id,
  label,
}: {
  count: number;
  id: string;
  label: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <h2 className="text-[13px] leading-[18px] font-semibold" id={id}>
        {label}
      </h2>
      <span className="text-[13px] font-medium text-neutral-500 tabular-nums">
        {count}
      </span>
    </div>
  );
}

function LoadMore({ feed }: { feed: Feed }) {
  const remaining = Math.max(feed.totalCount - feed.interviews.length, 0);
  if (!feed.hasNextPage || remaining === 0) return null;
  const step = Math.min(remaining, INTERVIEWS_PAGE_SIZE);
  return (
    <button
      className="hover:text-foreground w-fit cursor-pointer text-xs font-medium text-neutral-500 disabled:opacity-50"
      disabled={feed.isFetchingNextPage}
      onClick={() => void feed.fetchNextPage()}
      type="button"
    >
      Show {step} more
      {remaining > step && ` · ${remaining} hidden`}
      {feed.isFetchingNextPage && '…'}
      <span className="sr-only"> {pluralize('interview', step)}</span>
    </button>
  );
}
